import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, AlertTriangle, ArrowLeft, Mail, Lock } from "lucide-react";
import Logo from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // Already signed in? go straight to the dashboard
    (async () => {
      try {
        await axios.get(`${API}/auth/me`, { withCredentials: true });
        navigate("/admin", { replace: true });
      } catch {
        setChecking(false);
      }
    })();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim() || !password) {
      setError("Enter your email and password.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email: email.trim(), password },
        { withCredentials: true }
      );
      navigate("/admin", { replace: true, state: { user: data } });
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Sign-in failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-cream flex flex-col">
      <header className="px-6 md:px-12 h-20 flex items-center border-b border-brand-border">
        <Logo />
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="border border-brand-border bg-brand-ivory rounded-2xl p-10 shadow-[0_30px_70px_-30px_rgba(47,61,30,0.3)]">
            <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-brand-olive text-brand-cream">
              <ShieldCheck className="w-6 h-6" strokeWidth={1.6} />
            </span>
            <h1 className="font-display mt-7 text-3xl text-brand-deep font-light leading-tight">
              FFIB Admin Inbox
            </h1>
            <p className="mt-4 text-brand-muted text-sm leading-relaxed font-light">
              Sign in to review buyer and supplier submissions. Access is
              restricted to authorized FFIB team accounts.
            </p>

            {error && (
              <div
                data-testid="admin-login-error"
                className="mt-6 flex items-start gap-3 border border-red-300 bg-red-50 text-red-800 rounded-lg px-4 py-3 text-sm"
              >
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="mt-7 space-y-4" data-testid="admin-login-form" noValidate>
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="admin-email"
                  className="text-xs font-semibold tracking-[0.16em] uppercase text-brand-muted"
                >
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-quiet" />
                  <input
                    id="admin-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="username"
                    data-testid="admin-email-input"
                    placeholder="you@ingredientsignal.com"
                    className="w-full bg-brand-cream border border-brand-border rounded-xl pl-10 pr-4 py-3 text-sm text-brand-deep placeholder:text-brand-quiet/70 focus:outline-none focus:border-brand-olive focus:ring-1 focus:ring-brand-olive transition-colors"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label
                  htmlFor="admin-password"
                  className="text-xs font-semibold tracking-[0.16em] uppercase text-brand-muted"
                >
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-quiet" />
                  <input
                    id="admin-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    data-testid="admin-password-input"
                    placeholder="••••••••"
                    className="w-full bg-brand-cream border border-brand-border rounded-xl pl-10 pr-4 py-3 text-sm text-brand-deep placeholder:text-brand-quiet/70 focus:outline-none focus:border-brand-olive focus:ring-1 focus:ring-brand-olive transition-colors"
                  />
                </div>
              </div>

              <button
                type="submit"
                data-testid="admin-login-submit"
                disabled={submitting || checking}
                className="w-full inline-flex items-center justify-center gap-2 bg-brand-olive hover:bg-brand-hover text-brand-cream px-6 py-3.5 rounded-full text-sm font-medium transition-colors disabled:opacity-50 mt-2"
              >
                {submitting ? "Signing in…" : checking ? "Checking session…" : "Sign in"}
              </button>
            </form>
          </div>

          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 text-sm text-brand-muted hover:text-brand-deep transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to site
          </Link>
        </div>
      </main>
    </div>
  );
}
